import Call from "./Call";
import {useQuery} from "@tanstack/react-query";
import {getCallDetails, getRecentCalls} from "../api/calls";
import {useEffect, useMemo, useState} from "react";
import {PaginationAndFilter} from "./PaginationAndFilter";
import SelectedCallDetails from "./SelectedCallDetails";
import Maps from "./Maps";

const CallsContainer = () => {
    const debugMode = useMemo(() => {
        if (typeof window === "undefined") {
            return false;
        }

        return new URLSearchParams(window.location.search).get("d") === "1";
    }, []);
    const useAlsMarker = useMemo(() => {
        if (typeof window === "undefined") {
            return true;
        }

        return new URLSearchParams(window.location.search).get("als") !== "0";
    }, []);
    const [queryParams, setQueryParams] = useState({
        start: 0,
        pageSize: 10,
        from: null,
        to: null,
        lat: null,
        lng: null
    });
    const [selectedCallUuid, setSelectedCallUuid] = useState(null);
    const [autoRefreshMap, setAutoRefreshMap] = useState(true);
    const [showAllMapMarkers, setShowAllMapMarkers] = useState(false);
    const [showGpsMarkers, setShowGpsMarkers] = useState(false);
    const [selectedLogIndex, setSelectedLogIndex] = useState(null);

    const goPrevious = () => {
        let previousStart = queryParams.start - queryParams.pageSize;
        previousStart = previousStart < 0 ? 0 : previousStart;
        setQueryParams({...queryParams, start: previousStart, pageSize: queryParams.pageSize});
    };
    const goNext = () => {
        setQueryParams({
            ...queryParams,
            start: queryParams.start + queryParams.pageSize,
            pageSize: queryParams.pageSize
        });
    };
    const setFrom = (date) => setQueryParams({...queryParams, from: date});
    const setTo = (date) => setQueryParams({...queryParams, to: date});

    const clearDates = () => {
        setQueryParams({...queryParams, from: null, to: null, lat: null, lng: null});
    };

    const {isLoading, isRefetching, isError, data, refetch} = useQuery({
        queryKey: ["get-calls", {
            start: queryParams.start,
            pageSize: queryParams.pageSize,
            from: queryParams.from,
            to: queryParams.to,
            lat: queryParams.lat,
            lng: queryParams.lng
        }],
        queryFn: getRecentCalls,
        refetchInterval: 30000,
        refetchIntervalInBackground: true
    });

    const dataToRender = data || {hasNext: false, hasPrevious: false, calls: []};

    const {isLoading: isDetailLoading, isError: isDetailError, data: detailResponse} = useQuery({
        queryKey: ["get-call-detail-" + selectedCallUuid, {callUuid: selectedCallUuid}],
        queryFn: getCallDetails,
        enabled: !!selectedCallUuid,
        refetchInterval: 10000,
        refetchIntervalInBackground: true
    });

    const selectedCallDetail = useMemo(() => {
        if (!detailResponse?.length) {
            return null;
        }

        return {
            ...detailResponse[0],
            eml: (detailResponse[0].eml || []).map((callPushData) => ({
                ...callPushData,
                payload: (callPushData.payload || []).map((payloadItem) => ({
                    ...payloadItem,
                    rankedLocations: [...(payloadItem.rankedLocations || [])].sort((left, right) =>
                        left.confidence > right.confidence ? -1 : 1
                    )
                }))
            }))
        };
    }, [detailResponse]);

    useEffect(() => {
        setSelectedLogIndex(null);
    }, [selectedCallUuid]);

    return <div className="flex h-full min-h-0 w-full flex-col px-4 pb-4 sm:px-6 lg:px-8">
        <div className="mt-6 grid min-h-0 flex-1 gap-6 xl:grid-cols-[22rem_26rem_minmax(0,1fr)]">
            <section className="flex h-full min-h-[28rem] flex-col rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-200 px-5 py-4">
                    <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Calls</div>
                    <div className="mt-4">
                        <PaginationAndFilter
                            clearDates={clearDates}
                            fromDate={queryParams.from}
                            toDate={queryParams.to}
                            setFromDate={setFrom}
                            setToDate={setTo}
                            displayRefresh={true}
                            isLoading={isLoading || isRefetching}
                            isError={isError}
                            refreshFn={refetch}
                            goPrevious={goPrevious}
                            goNext={goNext}
                            hasNext={dataToRender.hasNext}
                            hasPrevious={dataToRender.hasPrevious}
                        />
                        <div className="mt-4 flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                            <span className="text-sm font-medium text-slate-600">Auto-Refresh</span>
                            <button
                                type="button"
                                aria-pressed={autoRefreshMap}
                                onClick={() => {
                                    const nextValue = !autoRefreshMap;
                                    setAutoRefreshMap(nextValue);
                                    if (nextValue) {
                                        setShowAllMapMarkers(false);
                                        setSelectedLogIndex(null);
                                    }
                                }}
                                className={`relative inline-flex h-7 w-12 items-center rounded-full transition ${
                                    autoRefreshMap ? "bg-blue-600" : "bg-slate-300"
                                }`}
                            >
                                <span
                                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
                                        autoRefreshMap ? "translate-x-6" : "translate-x-1"
                                    }`}
                                />
                            </button>
                        </div>
                        <div className="mt-3 flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                            <span className="text-sm font-medium text-slate-600">GPS</span>
                            <button
                                type="button"
                                aria-pressed={showGpsMarkers}
                                onClick={() => setShowGpsMarkers(!showGpsMarkers)}
                                className={`relative inline-flex h-7 w-12 items-center rounded-full transition ${
                                    showGpsMarkers ? "bg-blue-600" : "bg-slate-300"
                                }`}
                            >
                                <span
                                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
                                        showGpsMarkers ? "translate-x-6" : "translate-x-1"
                                    }`}
                                />
                            </button>
                        </div>
                    </div>
                </div>
                <div className="min-h-0 flex-1 space-y-3 overflow-y-scroll p-4">
                    {dataToRender.calls.map((call) => <Call
                        key={call.callUuid}
                        {...call}
                        isSelected={call.callUuid === selectedCallUuid}
                        onSelect={() => setSelectedCallUuid(call.callUuid)}
                    />)}
                </div>
            </section>
            <div className="h-full min-h-0 min-w-0">
                <SelectedCallDetails
                    call={selectedCallDetail}
                    isLoading={isDetailLoading}
                    isError={isDetailError}
                    autoRefreshMap={autoRefreshMap}
                    showAllMapMarkers={showAllMapMarkers}
                    debugMode={debugMode}
                    onToggleShowAll={(nextValue) => {
                        setShowAllMapMarkers(nextValue);
                        if (nextValue) {
                            setAutoRefreshMap(false);
                            setSelectedLogIndex(null);
                        }
                    }}
                    onSelectLog={(logIndex) => setSelectedLogIndex(logIndex)}
                />
            </div>
            <div className="h-full min-h-0 min-w-0">
                <Maps
                    call={selectedCallDetail}
                    autoRefreshMap={autoRefreshMap}
                    showAllMapMarkers={showAllMapMarkers}
                    showGpsMarkers={showGpsMarkers}
                    debugMode={debugMode}
                    selectedLogIndex={selectedLogIndex}
                    useAlsMarker={useAlsMarker}
                />
            </div>
        </div>
    </div>;
};

export default CallsContainer;
